import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom"; // Adaugă useNavigate
import {
  getFirestore,
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import Box from "@mui/material/Box";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faShoppingCart } from "@fortawesome/free-solid-svg-icons";
import { useCart } from "../../context/CartContext";
import "./ProductPage.css";

const db = getFirestore();

const ProductPage = () => {
  const { id } = useParams();
  const navigate = useNavigate(); // Inițializează navigate
  const { cart, updateCartCount } = useCart();
  const [product, setProduct] = useState(null);
  const [similarProducts, setSimilarProducts] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [zoomStyle, setZoomStyle] = useState({});
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const fetchProduct = async () => {
      if (!id) {
        console.log("No product ID found in URL.");
        return;
      }

      try {
        const docRef = doc(db, "Products", id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const productData = docSnap.data();
          setProduct(productData);
          setSelectedImage(productData.imageURL[0]);

          // Fetch similar products
          const category = productData.category;
          if (category) {
            const productsQuery = query(
              collection(db, "Products"),
              where("category", "==", category)
            );
            const querySnapshot = await getDocs(productsQuery);
            const productsList = querySnapshot.docs
              .map((doc) => ({ id: doc.id, ...doc.data() }))
              .filter((item) => item.id !== id)
              .slice(0, 3);
            setSimilarProducts(productsList);
          }
        } else {
          console.log("Product not found");
        }
      } catch (error) {
        console.error("Error fetching product:", error);
      }
    };

    fetchProduct();
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [id]);

  const handleThumbnailClick = (url, index) => {
    setSelectedImage(url);
    setCurrentImageIndex(index);
  };

  const handleZoom = (e) => {
    const imageElement = e.target;
    const bounds = imageElement.getBoundingClientRect();

    const offsetX = e.clientX - bounds.left;
    const offsetY = e.clientY - bounds.top;

    const zoomBackgroundPositionX = (offsetX / bounds.width) * 100;
    const zoomBackgroundPositionY = (offsetY / bounds.height) * 100;

    setZoomStyle({
      backgroundImage: `url(${selectedImage})`,
      backgroundPosition: `${zoomBackgroundPositionX}% ${zoomBackgroundPositionY}%`,
      visibility: "visible",
    });
  };

  const handleMouseLeave = () => {
    setZoomStyle({
      visibility: "hidden",
    });
  };

  const handleAddToCart = () => {
    if (id) {
      // Verifică dacă produsul este deja în coș
      const isAlreadyInCart = cart.includes(id);
      if (isAlreadyInCart) {
        toast.info("Produsul este deja în coș", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: false,
          closeButton: false,
        });
        return;
      }

      // Adaugă produsul în coș dacă nu există deja
      const newCart = [...cart, id];
      console.log("Produs adăugat:", newCart);
      updateCartCount(newCart); // Actualizează contextul cu noul coș

      toast.success("Produs adăugat în coș", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: false,
        closeButton: false,
      });
    }
  };

  const handleProductClick = (productId) => {
    navigate(`/item/${productId}`); // Navighează la pagina produsului selectat
  };

  if (!product || !product.imageURL || product.imageURL.length === 0) {
    return <p>Loading product...</p>;
  }

  return (
    <Box className="product-page">
      <Box className="product-page-main">
        <Box className="product-page-image-section">
          <Box className="product-page-thumbnails">
            {product.imageURL.map((url, index) => (
              <img
                key={index}
                src={url}
                alt={`Thumbnail ${index}`}
                onClick={() => handleThumbnailClick(url, index)}
                className={`product-page-thumbnail ${
                  url === selectedImage ? "active" : ""
                }`}
              />
            ))}
          </Box>

          <Box className="product-page-main-image-wrapper">
            <img
              src={selectedImage}
              alt="Selected"
              className="product-page-main-image"
              onMouseMove={handleZoom}
              onMouseLeave={handleMouseLeave}
            />
            <Box className="product-page-main-image-zoom" style={zoomStyle} />
          </Box>
        </Box>

        <Box className="product-page-details">
          <h1 className="product-page-title">{product.name}</h1>
          <p className="product-page-price">{product.price} RON</p>
          <button
            className="product-page-add-to-cart"
            onClick={handleAddToCart}
          >
            <FontAwesomeIcon
              icon={faShoppingCart}
              style={{ marginRight: "8px" }}
            />
            Add to Cart
          </button>
          <ToastContainer />
          <div className="product-page-description">
            <p>{product.description}</p>
          </div>
          <div className="product-page-delivery-payment">
            <h2 className="product-page-delivery-payment-title">
              Plata și livrare
            </h2>
            <p>
              Plata se poate realiza prin transfer bancar, detaliile fiind
              oferite la finalizarea comenzii. Odată ce plata va fi confirmată
              de către noi, comanda va fi dată în lucru. Produsele se pot ridica
              personal de la adresa oferită în formularul de comandă, sau
              acestea pot fi livrate prin curier, implicând un cost adițional,
            </p>
          </div>
        </Box>
      </Box>

      <Box className="product-page-similar-products">
        <h2>Produse din aceeași categorie</h2>
        <Box className="product-page-similar-products-container">
          {similarProducts.map((prod) => (
            <Box
              key={prod.id}
              className="product-page-similar-product-card"
              onClick={() => handleProductClick(prod.id)} // Adaugă evenimentul de click
            >
              <img src={prod.imageURL[0]} alt={prod.name} />
              <h3>{prod.name}</h3>
              <p>{prod.price} RON</p>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default ProductPage;
